import React from 'react';

import { Link } from 'toolkit/chakra/link';

import { number } from './format';
import Icon from './Icon';
import styles from './SabaHome.module.css';

export interface ChartPoint {
  date: string;
  value: number | null;
}
interface Props {
  href?: string;
  title: string;
  points: Array<ChartPoint>;
  loading: boolean;
  error: boolean;
  description: string;
  retry: () => void;
}

export default function Chart({ title, points, loading, error, description, retry, href = '/txs' }: Props) {
  const gradientId = React.useId().replace(/:/g, '');
  const [ days, setDays ] = React.useState(30);
  const sorted = points.filter((point) => Number.isFinite(Date.parse(point.date))).sort((a, b) => a.date.localeCompare(b.date));
  const lastDate = sorted.at(-1)?.date;
  const cutoff = lastDate ? Date.parse(lastDate) - (days - 1) * 86400000 : 0;
  const selected = sorted.filter((point) => Date.parse(point.date) >= cutoff);
  const valid = selected.filter((point) => point.value !== null && Number.isFinite(point.value));
  const max = Math.ceil((Math.max(1, ...valid.map((point) => point.value as number)) * 1.15) / 4) * 4;
  const x = (point: ChartPoint) =>
    62 +
    (lastDate && Date.parse(lastDate) > cutoff ? (Date.parse(point.date) - cutoff) / (Date.parse(lastDate) - cutoff) : 0.5) * 588;
  const y = (value: number) => 212 - (value / max) * 178;
  const segments: Array<Array<ChartPoint>> = [];
  selected.forEach((point, index) => {
    if (point.value === null || !Number.isFinite(point.value)) return;
    const previous = selected[index - 1];
    if (
      !previous ||
      previous.value === null ||
      !Number.isFinite(previous.value) ||
      Date.parse(point.date) - Date.parse(previous.date) > 86400000
    ) {
      segments.push([]);
    }
    if (!segments.length) segments.push([]);
    segments[segments.length - 1].push(point);
  });
  let message = 'هنوز داده‌ای برای این نمودار در دسترس نیست';
  if (error) message = 'دریافت نمودار ناموفق بود';
  if (loading) message = 'در حال دریافت آمار…';
  const handlePeriod = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => setDays(Number(event.currentTarget.value)),
    [],
  );
  return (
    <section className={ styles.panel } aria-label={ title }>
      <div className={ styles.panelHeading }>
        <h2>{ title }</h2>
        <span title={ description } className={ styles.info }>
          i
        </span>
        <Link href={ href }>
          مشاهده بیشتر <Icon name="arrow" size={ 16 }/>
        </Link>
      </div>
      <div className={ styles.chartToolbar }>
        <span>
          { valid.length ? number(valid.at(-1)?.value, 0) : '—' } <small>امروز</small>
        </span>
        <div className={ styles.periods } aria-label={ `بازه ${ title }` }>
          { [ 30, 365 ].map((period) => (
            <button key={ period } type="button" aria-pressed={ days === period } value={ period } onClick={ handlePeriod }>
              { period === 365 ? 'یک ساله' : '۳۰ روزه' }
            </button>
          )) }
        </div>
      </div>
      { loading || error || !valid.length ? (
        <div className={ styles.chartEmpty } role="status">
          <Icon name="chart" size={ 32 }/>
          <strong>{ message }</strong>
          <span>{ error ? 'برای دریافت آمار شبکه دوباره تلاش کنید.' : 'نمودار با دریافت داده‌های شبکه نمایش داده می‌شود.' }</span>
          { error && (
            <button type="button" onClick={ retry }>
              تلاش دوباره
            </button>
          ) }
        </div>
      ) : (
        <>
          <svg className={ styles.chart } viewBox="0 0 680 252" role="img" aria-label={ `${ title } در ${ number(days, 0) } روز` }>
            <defs>
              <linearGradient id={ gradientId } x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#82afea" stopOpacity="0.28"/>
                <stop offset="100%" stopColor="#82afea" stopOpacity="0.015"/>
              </linearGradient>
            </defs>
            { [ 0, 1, 2, 3, 4 ].map((tick) => (
              <g key={ tick }>
                <line x1="62" x2="650" y1={ y((max * tick) / 4) } y2={ y((max * tick) / 4) } stroke="#e9edf2" strokeDasharray="4 5"/>
                <text x="50" y={ y((max * tick) / 4) + 4 } textAnchor="end">
                  { number((max * tick) / 4, 0) }
                </text>
              </g>
            )) }
            { segments.map((segment, index) => {
              const line = segment.map((point, i) => `${ i ? 'L' : 'M' } ${ x(point) } ${ y(point.value as number) }`).join(' ');
              return (
                <g key={ index }>
                  <path
                    d={ `${ line } L ${ x(segment[segment.length - 1]) } 212 L ${ x(segment[0]) } 212 Z` }
                    fill={ `url(#${ gradientId })` }
                  />
                  <path d={ line } fill="none" stroke="#79a3d4" strokeWidth="1.6"/>
                </g>
              );
            }) }
            { valid.map((point, index) => (
              <g key={ point.date }>
                <circle cx={ x(point) } cy={ y(point.value as number) } r={ valid.length === 1 ? 4 : 2 } fill="#79a3d4">
                  <title>
                    { point.date }: { number(point.value, 0) }
                  </title>
                </circle>
                { (index === valid.length - 1 ||
                  (index % Math.max(1, Math.ceil(valid.length / 6)) === 0 &&
                    index < valid.length - Math.ceil(valid.length / 12))) && (
                  <text x={ x(point) } y="240" textAnchor="middle">
                    { new Intl.DateTimeFormat('fa-IR', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
                      new Date(point.date),
                    ) }
                  </text>
                ) }
              </g>
            )) }
          </svg>
          <details className={ styles.chartData }>
            <summary>داده‌های نمودار</summary>
            <table>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>تعداد</th>
                </tr>
              </thead>
              <tbody>
                { selected.map((point) => (
                  <tr key={ point.date }>
                    <td>{ point.date }</td>
                    <td>{ number(point.value, 0) }</td>
                  </tr>
                )) }
              </tbody>
            </table>
          </details>
        </>
      ) }
    </section>
  );
}
